import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Box, DialogContent } from '@mui/material';
import './auth-dialog.scss';
import { AuthHeader } from './components/auth-header/auth-header.tsx';
import { useState } from 'react';
import { LoginForm } from './components/login-form/login-form.tsx';
import { VerifyAccount } from './components/verify-account/verify-account.tsx';
import { ResetPasswordForm } from './components/reset-password/reset-password-form.tsx';
import { ResetPasswordNewPasswordForm } from './components/reset-password/reset-password-new-password.tsx';
import { ResetPasswordSuccessSnackbar } from './components/reset-password/reset-password-success-snackbar.tsx';
import {
  FirstStepSignUpFormValues,
  LoginFormValues,
  UpdatePasswordFormValues,
  ResetPasswordFormValues,
  AuthFormValues,
} from './utils/form-validation.ts';
import {
  useCreateAccount,
  useGetCurrentUser,
  useRequestResetPasswordCode,
  useSignIn,
  useUpdatePassword,
} from '../../hooks/auth.ts';
import { SignInInput, SignUpInput } from 'aws-amplify/auth';
import { SignupBuildInfo } from './components/signup-build-info/signup-build-info.tsx';
import { SignupUserInfo } from './components/signup-user-info/signup-user-info.tsx';

interface AuthDialogProps {
  payload?: string;
  open: boolean;
  onClose: () => void;
}

// Payload is the page to show when the dialog is opened.
export const AuthDialog = ({ payload, open, onClose }: AuthDialogProps) => {
  const theme = useTheme();
  const { data: user } = useGetCurrentUser();

  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [activePage, setActivePage] = useState(payload || '');
  const [email, setEmail] = useState('');
  const [showingResetPasswordToast, setShowingResetPasswordToast] =
    useState(false);

  const [firstStepSignUpValues, setFirstStepSignUpValues] = useState<
    FirstStepSignUpFormValues | undefined
  >(undefined);

  const [addedCities, setAddedCities] = useState<string[]>([]);

  const handleClose = () => {
    setActivePage('login');
    onClose();
  };

  const signInMutation = useSignIn();

  const signIn = (values: LoginFormValues) => {
    const req: SignInInput = {
      username: values.email,
      password: values.password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    };
    signInMutation.mutate(req, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const resetPasswordMutation = useUpdatePassword();

  const resetPassword = (values: UpdatePasswordFormValues) => {
    const req = {
      username: email,
      password: values.newPassword,
      confirmationCode: values.verificationCode,
    };
    resetPasswordMutation.mutate(req, {
      onSuccess: () => {
        setShowingResetPasswordToast(true);
        setActivePage('login');
      },
    });
  };

  const requestResetMutation = useRequestResetPasswordCode();

  const requestReset = (values: ResetPasswordFormValues) => {
    const req = {
      username: values.username,
    };
    requestResetMutation.mutate(req, {
      onSuccess: () => {
        setEmail(values.username);
        setActivePage('new_password');
      },
    });
  };

  const createAccountMutation = useCreateAccount();

  const createAccount = (values: AuthFormValues) => {
    if (firstStepSignUpValues) {
      values.firstName = firstStepSignUpValues.firstName;
      values.lastName = firstStepSignUpValues.lastName;
      values.email = firstStepSignUpValues.email;
      values.phone = firstStepSignUpValues.phone;
      values.password = firstStepSignUpValues.password;
      values.passwordConfirm = firstStepSignUpValues.password;
      values.textOrEmailPreference =
        firstStepSignUpValues.textOrEmailPreference;
    }

    const req: SignUpInput = {
      username: values.email,
      password: values.password,
      options: {
        userAttributes: {
          // Required
          email: values.email,
          phone_number: `+1${values.phone}`, // TODO: we need to guide the user toward E.164 format
          given_name: values.firstName,
          family_name: values.lastName,
        },
        validationData: {},
        clientMetadata: {
          // Required
          contactPreferences: values.textOrEmailPreference,
          stateBuildLocation: values.stateBuildLocation,
          citiesBuildLocation: [
            ...addedCities,
            values.citiesBuildLocation,
          ].join(', '),
          needHomeLoan: values.needHomeLoan,
          needLand: values.needLand,
          needHome: values.needHome,
          totalBudget: values.totalBudget.replace(/[$,]/g, ''),
          // Optional
          annualGrossIncome:
            values.annualGrossIncome?.replace(/[$,]/g, '') || '',
          creditScore: values.creditScore || '',
          isSecondaryResidence: values.isSecondaryResidence || '',
          homeStylePreference: values.homeStylePreference || '',
          homeStylePreferenceOtherDescription:
            values.homeStylePreferenceOtherDescription || '',
          freeFormAdditionalDetails: values.freeFormAbout || '',
        },
      },
    };
    createAccountMutation.mutate(req, {
      onSuccess: () => {
        setActivePage('verify');
      },
    });
  };

  // TODO: We may just want to use the router for this.
  const showActivePage = () => {
    if (activePage === 'auth') {
      return (
        <SignupUserInfo
          setActivePage={setActivePage}
          firstStepSignUpValues={firstStepSignUpValues}
          setFirstStepSignUpValues={setFirstStepSignUpValues}
        />
      );
    } else if (activePage === 'auth2') {
      return (
        <SignupBuildInfo
          isPending={createAccountMutation.isPending}
          postSubmitErrors={createAccountMutation.error?.message}
          addedCities={addedCities}
          setAddedCities={setAddedCities}
          createAccount={createAccount}
        />
      );
    } else if (activePage === 'verify') {
      return <VerifyAccount setActivePage={setActivePage} />;
    } else if (activePage === 'forgot_password') {
      return (
        <ResetPasswordForm
          setActivePage={setActivePage}
          requestReset={requestReset}
          isPending={requestResetMutation.isPending}
          postSubmitErrors={requestResetMutation.error?.message || ''}
          userId={user?.userId}
        />
      );
    } else if (activePage === 'new_password') {
      return (
        <ResetPasswordNewPasswordForm
          setActivePage={setActivePage}
          resetPassword={resetPassword}
          isPending={resetPasswordMutation.isPending}
          postSubmitErrors={resetPasswordMutation.error?.message || ''}
          userId={user?.userId}
        />
      );
    } else {
      return (
        <LoginForm
          signIn={signIn}
          setActivePage={setActivePage}
          postSubmitErrors={signInMutation.error?.message || ''}
          isPending={signInMutation.isPending}
        />
      );
    }
  };

  return (
    <>
      <Dialog
        data-testid="auth-dialog"
        className="auth-dialog"
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby="auth-dialog-title"
      >
        <Box className="container">
          <AuthHeader
            onClose={onClose}
            activePage={activePage}
            setActivePage={setActivePage}
            user={user}
          />
          <DialogContent className="content">{showActivePage()}</DialogContent>
          <ResetPasswordSuccessSnackbar
            isOpen={showingResetPasswordToast}
            onClose={() => setShowingResetPasswordToast(false)}
          />
        </Box>
      </Dialog>
    </>
  );
};
